import { ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  CreateEventIdeaRequest,
  EventIdeaLocationHint,
  EventIdeaResource,
  IdeaTiming,
  IdeaTimingKind,
  UpdateEventIdeaRequest,
} from '@@/types/api';
import type { CropRect } from '@@/types/api/common';
import type { CoverArtState } from '~/utils/cover-art-state';
import {
  applyFocalPoint,
  applyNaturalDimensions,
  applyOverrideCrop,
  applyResetAspectOverride,
  initialCoverArtState,
  revokeBlobUrl,
} from '~/utils/cover-art-state';
import { buildIdeaTiming, validateIdeaTiming } from '~/utils/idea-timing';
import { validateIdeaLocation } from '~/utils/idea-validation';

// ---------------------------------------------------------------------------
// Event Idea authoring form store
//
// Modelled on `use-submission-funnel-store.ts`: a flat `ref<string>` per field
// (Vuetify text/select inputs bind strings), no nested reactive object, and a
// hand-written `$reset()`. All timing/location semantics live in the pure
// `idea-timing` / `idea-validation` utils — this store only marshals form state
// into (and out of) the backend payload shapes.
//
// The store doubles as the `CoverArtController` for `CommonCoverImageUpload`,
// exactly as the native event-form store does: setup stores unwrap refs, so
// `store.coverArt` is a plain reactive object and the five mutations are
// already on the proxy. Call sites are just `:controller="store"`.
// ---------------------------------------------------------------------------

export type IdeaFormMode = 'create' | 'edit';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function trimmed(value: string): string {
  return value.trim();
}

function timingEquals(a: IdeaTiming, b: IdeaTiming): boolean {
  return (
    a.kind === b.kind &&
    a.display === b.display &&
    a.starts_at === b.starts_at &&
    a.ends_at === b.ends_at
  );
}

function locationEquals(
  a: EventIdeaLocationHint,
  b: EventIdeaLocationHint,
): boolean {
  return (
    a.display === b.display &&
    a.latitude === b.latitude &&
    a.longitude === b.longitude
  );
}

export const useIdeaFormStore = defineStore('ideaForm', () => {
  const mode = ref<IdeaFormMode>('create');
  const editIdeaId = ref<string | null>(null);

  // --- Form fields (string refs; Vuetify binds strings) ---
  const title = ref('');
  const description = ref('');

  // Rough-timing precision. `season` is the default because an idea is a pitch
  // for "sometime around then" — it forces an explicit, honest choice rather
  // than pre-filling a date the author never picked.
  const timingKind = ref<IdeaTimingKind>('season');
  const startDate = ref(''); // Y-m-d — `exact` only
  const endDate = ref(''); // Y-m-d — `exact` only, blank means single day
  const month = ref(''); // 1-12 — `month` only
  const season = ref(''); // one of IDEA_SEASONS — `season` only
  const year = ref(''); // four-digit — month / season / year

  // Location. `display` is Vuetify-bound (a string); the coordinates are NOT —
  // they arrive as numbers from a genuine Places pick and stay null for free
  // text, which is exactly what `validateIdeaLocation` rejects [S1].
  const locationDisplay = ref('');
  const latitude = ref<number | null>(null);
  const longitude = ref<number | null>(null);

  // --- Cover art (this store is the CoverArtController) ---
  const coverArt = ref<CoverArtState>(initialCoverArtState());

  // The idea's already-published cover, when editing. `EventIdeaResource`
  // exposes a FLAT `cover_image_url` with no focal-point/crops envelope, so an
  // existing cover can be displayed and replaced but never rehydrated into the
  // crop editor — see the copy on the edit page.
  const existingCoverImageUrl = ref<string | null>(null);

  // --- Submit state ---
  const serverErrors = ref<Record<string, string[]>>({});
  const isSubmitting = ref(false);

  // Idempotency key — minted lazily per submit attempt and preserved across
  // retries of that same attempt.
  const idempotencyKey = ref('');

  function ensureIdempotencyKey(): string {
    if (!idempotencyKey.value) {
      idempotencyKey.value = crypto.randomUUID();
    }
    return idempotencyKey.value;
  }

  function resetIdempotencyKey() {
    idempotencyKey.value = '';
  }

  // ---------------------------------------------------------------------------
  // Cover-art mutations (CoverArtController) — one-line delegations to the pure
  // appliers, identical to the native event-form store.
  // ---------------------------------------------------------------------------

  function setFocalPoint(focal: { x: number; y: number }) {
    applyFocalPoint(coverArt.value, focal);
  }

  function setNaturalDimensions(dims: { w: number; h: number }) {
    applyNaturalDimensions(coverArt.value, dims);
  }

  function setOverrideCrop(aspect: '16_9' | '1_1', rect: CropRect) {
    applyOverrideCrop(coverArt.value, aspect, rect);
  }

  function resetAspectOverride(aspect: '16_9' | '1_1') {
    applyResetAspectOverride(coverArt.value, aspect);
  }

  function clearCoverArt() {
    revokeBlobUrl(coverArt.value.originalUrl);
    // Object-identity replacement is load-bearing: it's what the `deep`
    // watchers in CoverImageUpload observe.
    coverArt.value = initialCoverArtState();
  }

  // ---------------------------------------------------------------------------
  // Payload builders
  // ---------------------------------------------------------------------------

  function buildTiming(): IdeaTiming {
    return buildIdeaTiming({
      kind: timingKind.value,
      startDate: startDate.value,
      endDate: endDate.value,
      month: month.value,
      season: season.value,
      year: year.value,
    });
  }

  /**
   * The `location_hint` sub-object. Returns null until a genuine autocomplete
   * pick has supplied coordinates — the sub-object is all-or-nothing, so a
   * half-filled hint is never sent.
   */
  function buildLocationHint(): EventIdeaLocationHint | null {
    if (latitude.value === null || longitude.value === null) return null;
    return {
      display: trimmed(locationDisplay.value),
      latitude: latitude.value,
      longitude: longitude.value,
    };
  }

  function buildCreatePayload(): CreateEventIdeaRequest {
    return {
      title: trimmed(title.value),
      description: trimmed(description.value),
      // Validation gates submit, so by the time this runs the hint exists. The
      // fallback keeps the shape total rather than throwing mid-submit; the
      // backend 422s on the missing coordinates if it is ever reached.
      location_hint: buildLocationHint() ?? {
        display: trimmed(locationDisplay.value),
        latitude: Number.NaN,
        longitude: Number.NaN,
      },
      timing: buildTiming(),
    };
  }

  // The payload the form was hydrated from, kept so `buildUpdatePayload` can
  // send ONLY what changed (PATCH is partial).
  const originalPayload = ref<CreateEventIdeaRequest | null>(null);

  /**
   * Partial update body. `PATCH` accepts every field as `sometimes`, so only
   * changed keys are sent — but `location_hint` and `timing` are all-or-nothing
   * sub-objects, so each is included whole or not at all.
   */
  function buildUpdatePayload(): UpdateEventIdeaRequest {
    const next = buildCreatePayload();
    const original = originalPayload.value;
    if (!original) return next;

    const payload: UpdateEventIdeaRequest = {};

    if (next.title !== original.title) payload.title = next.title;
    if (next.description !== original.description) {
      payload.description = next.description;
    }
    if (!locationEquals(next.location_hint, original.location_hint)) {
      payload.location_hint = next.location_hint;
    }
    if (!timingEquals(next.timing, original.timing)) {
      payload.timing = next.timing;
    }

    return payload;
  }

  // ---------------------------------------------------------------------------
  // Local validation — merges the two pure validators into the same
  // `serverErrors` shape a 422 populates, keyed by backend field paths.
  // ---------------------------------------------------------------------------

  function validateLocal(): Record<string, string[]> {
    const errors: Record<string, string[]> = {};

    for (const [field, message] of Object.entries(
      validateIdeaLocation({
        display: locationDisplay.value,
        latitude: latitude.value,
        longitude: longitude.value,
      }),
    )) {
      errors[field] = [message];
    }

    for (const [field, message] of Object.entries(
      validateIdeaTiming(buildTiming()),
    )) {
      errors[field] = [message];
    }

    return errors;
  }

  // ---------------------------------------------------------------------------
  // loadFromIdea — prefill the edit form from an existing resource
  // ---------------------------------------------------------------------------

  function loadFromIdea(idea: EventIdeaResource) {
    $reset();

    mode.value = 'edit';
    editIdeaId.value = idea.id;

    title.value = idea.title;
    description.value = idea.description;

    timingKind.value = idea.timing.kind;
    if (idea.timing.kind === 'exact') {
      // The datetimes round-trip through the Y-m-d date inputs; the minted
      // `display` is rebuilt from them on save, never parsed back out.
      startDate.value = (idea.timing.starts_at ?? '').slice(0, 10);
      endDate.value = (idea.timing.ends_at ?? '').slice(0, 10);
    }
    // Coarse kinds carry no datetimes, so the sub-field the author originally
    // chose (month / season / year) isn't recoverable from the resource — only
    // the minted `display` is. Parse the pieces back out of it where the shape
    // is unambiguous ("June 2027", "Summer 2027", "2027").
    hydrateCoarseTimingFromDisplay(idea.timing);

    locationDisplay.value = idea.location_hint.display;
    latitude.value = idea.location_hint.latitude;
    longitude.value = idea.location_hint.longitude;

    existingCoverImageUrl.value = idea.cover_image_url;

    originalPayload.value = {
      title: idea.title,
      description: idea.description,
      location_hint: { ...idea.location_hint },
      timing: { ...idea.timing },
    };
  }

  function hydrateCoarseTimingFromDisplay(timing: IdeaTiming) {
    const parts = timing.display.trim().split(/\s+/);
    const maybeYear = parts[parts.length - 1] ?? '';
    const hasYear = /^\d{4}$/.test(maybeYear);

    if (timing.kind === 'month' && hasYear) {
      const index = MONTH_NAMES.indexOf(parts[0] ?? '');
      if (index >= 0) month.value = String(index + 1);
      year.value = maybeYear;
      return;
    }
    if (timing.kind === 'season' && hasYear) {
      season.value = parts[0] ?? '';
      year.value = maybeYear;
      return;
    }
    if (timing.kind === 'year' && hasYear) {
      year.value = maybeYear;
    }
  }

  // ---------------------------------------------------------------------------
  // $reset
  // ---------------------------------------------------------------------------

  function $reset() {
    mode.value = 'create';
    editIdeaId.value = null;

    title.value = '';
    description.value = '';

    timingKind.value = 'season';
    startDate.value = '';
    endDate.value = '';
    month.value = '';
    season.value = '';
    year.value = '';

    locationDisplay.value = '';
    latitude.value = null;
    longitude.value = null;

    revokeBlobUrl(coverArt.value.originalUrl);
    coverArt.value = initialCoverArtState();
    existingCoverImageUrl.value = null;

    originalPayload.value = null;
    serverErrors.value = {};
    isSubmitting.value = false;
    idempotencyKey.value = '';
  }

  return {
    // mode
    mode,
    editIdeaId,
    // fields
    title,
    description,
    timingKind,
    startDate,
    endDate,
    month,
    season,
    year,
    locationDisplay,
    latitude,
    longitude,
    // cover art
    coverArt,
    existingCoverImageUrl,
    // submit state
    serverErrors,
    isSubmitting,
    idempotencyKey,
    originalPayload,
    // cover-art controller
    setFocalPoint,
    setNaturalDimensions,
    setOverrideCrop,
    resetAspectOverride,
    clearCoverArt,
    // actions
    ensureIdempotencyKey,
    resetIdempotencyKey,
    buildTiming,
    buildLocationHint,
    buildCreatePayload,
    buildUpdatePayload,
    validateLocal,
    loadFromIdea,
    $reset,
  };
});
